import PropTypes from 'prop-types';
import { getUsageColor, SUPPORT_EMAIL } from '../../utils/constants/subscription';
import GeneralLoader from '../loaders/GeneralLoader';
import { DOWNLOADS_ARE_PERIOD_SCOPED, formatPeriodDate } from '../../utils/contractPeriods';

const PLACEHOLDER = '—';

/**
 * TD-2813: built as one string rather than as JSX text around an expression,
 * which joins its lines with a space and would put one before the full stop.
 */
const redownloadNote = ({ resetsOn }) => {
  const base = 'Companies you downloaded in an earlier contract period count again';
  if (!resetsOn) return `${base}.`;
  return `${base}, and this period's allowance resets on ${formatPeriodDate(resetsOn)}.`;
};

// TD-2813: the period-scoped wording applies when the contract has more than
// one period AND the backend counts downloads per period (TD-2744, recorded by
// DOWNLOADS_ARE_PERIOD_SCOPED). Saying "this period" over a lifetime figure
// would be worse than saying nothing, so both halves have to hold.
const isPeriodScoped = (period) => DOWNLOADS_ARE_PERIOD_SCOPED && !!period?.isMultiPeriod;

// The object of the shortfall sentence: "contacts for all 60 companies" /
// "contacts for 1 company" for the contact list, "all 60 selected companies" /
// "1 selected company" otherwise.
const shortfallTarget = (totalCompanies, isContactsExport) => {
  const qualifier = isContactsExport ? '' : 'selected ';
  const companies =
    totalCompanies === 1 ? `1 ${qualifier}company` : `all ${totalCompanies} ${qualifier}companies`;
  return isContactsExport ? `contacts for ${companies}` : companies;
};

/**
 * The figures of the export: totals, what counts, and what is left after it.
 * Split out of the dialog so each half stays readable; it only renders.
 */
const ExportBreakdown = ({
  isLoading,
  didFail,
  isContactsExport,
  periodScoped,
  totalCompanies,
  alreadyDownloaded,
  netNew,
  remainingAfter,
  isInsufficient,
  color
}) => {
  if (isLoading) {
    return (
      <div className="bulk-export-breakdown" id="bulk-export-breakdown">
        <GeneralLoader text="Checking download availability…" />
      </div>
    );
  }
  return (
    <div className="bulk-export-breakdown" id="bulk-export-breakdown">
      <div className="bulk-export-row">
        <span>{isContactsExport ? 'Companies with contacts' : 'Total companies selected'}</span>
        <span className="bulk-export-value">{didFail ? PLACEHOLDER : totalCompanies}</span>
      </div>
      <div className="bulk-export-row">
        {/* TD-2813: on a contract with more than one period, "already
            downloaded" means already downloaded IN THIS PERIOD — a company
            opened during last year's term counts again. With a single
            period the two readings are identical, so the wording only
            changes when it has to. */}
        <span>{periodScoped ? 'Already downloaded this period' : 'Already downloaded'}</span>
        <span className="bulk-export-value">{didFail ? PLACEHOLDER : alreadyDownloaded}</span>
      </div>
      <div className="bulk-export-row bulk-export-row-highlight">
        <span>Net new downloads</span>
        <span className="bulk-export-value">{didFail ? PLACEHOLDER : netNew}</span>
      </div>
      <div className={`bulk-export-row bulk-export-row-result bulk-export-${color}`}>
        <span>Remaining after export</span>
        <span className="bulk-export-value">
          {didFail || isInsufficient ? PLACEHOLDER : remainingAfter}
        </span>
      </div>
    </div>
  );
};

ExportBreakdown.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  didFail: PropTypes.bool.isRequired,
  isContactsExport: PropTypes.bool.isRequired,
  periodScoped: PropTypes.bool.isRequired,
  totalCompanies: PropTypes.number,
  alreadyDownloaded: PropTypes.number,
  netNew: PropTypes.number,
  remainingAfter: PropTypes.number,
  isInsufficient: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired
};

const BulkExportDialog = ({
  totalCompanies = 0,
  alreadyDownloaded = 0,
  period = null,
  netNew = 0,
  currentCount = null,
  limit = 0,
  remainingAfter = 0,
  allowed = true,
  isLoading = false,
  didFail = false,
  isContactsExport = false,
  onConfirm,
  onCancel
}) => {
  const periodScoped = isPeriodScoped(period);
  const isInsufficient = !isLoading && !didFail && allowed === false;
  const usageAfter = (currentCount ?? 0) + netNew;
  const shortfall = isInsufficient ? Math.max(0, usageAfter - limit) : 0;
  const color = isInsufficient ? 'red' : getUsageColor(usageAfter, limit);

  return (
    <div className="bulk-export-dialog">
      <h3 id="bulk-export-title">Bulk Export Confirmation</h3>

      <ExportBreakdown
        isLoading={isLoading}
        didFail={didFail}
        isContactsExport={isContactsExport}
        periodScoped={periodScoped}
        totalCompanies={totalCompanies}
        alreadyDownloaded={alreadyDownloaded}
        netNew={netNew}
        remainingAfter={remainingAfter}
        isInsufficient={isInsufficient}
        color={color}
      />

      {didFail && (
        <div className="bulk-export-estimate-note">
          Preview unavailable - proceed to download to see updated counts.
        </div>
      )}

      {periodScoped && !didFail && (
        <div className="bulk-export-estimate-note">{redownloadNote(period)}</div>
      )}

      {isInsufficient && (
        <div className="bulk-export-insufficient" role="alert">
          <div className="bulk-export-insufficient-title">Insufficient downloads</div>
          <div className="bulk-export-insufficient-detail">
            You need {shortfall} more download{shortfall === 1 ? '' : 's'} to export{' '}
            {shortfallTarget(totalCompanies, isContactsExport)}.
          </div>
          <div className="bulk-export-insufficient-actions">
            <button className="bulk-export-btn-exclude" onClick={onCancel} type="button">
              Exclude more companies
            </button>{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} aria-label={`Email support at ${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      )}

      <div className="bulk-export-actions">
        <button className="bulk-export-btn-cancel" onClick={onCancel} type="button">
          Cancel
        </button>
        <button
          className="bulk-export-btn-confirm"
          onClick={onConfirm}
          disabled={isLoading || isInsufficient}
          type="button"
        >
          Confirm Export
        </button>
      </div>
    </div>
  );
};

BulkExportDialog.propTypes = {
  totalCompanies: PropTypes.number,
  alreadyDownloaded: PropTypes.number,
  // Shape mirrors useSubscriptionStatus().period: present for every migrated
  // company, null only for one without periods, and isMultiPeriod says whether
  // the wording below applies.
  period: PropTypes.shape({
    isMultiPeriod: PropTypes.bool,
    resetsOn: PropTypes.string
  }),
  netNew: PropTypes.number,
  currentCount: PropTypes.number,
  limit: PropTypes.number,
  remainingAfter: PropTypes.number,
  allowed: PropTypes.bool,
  isLoading: PropTypes.bool,
  didFail: PropTypes.bool,
  /** Contact-list export: `totalCompanies` counts only companies with contacts. */
  isContactsExport: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default BulkExportDialog;
